import React, { useEffect, useState } from "react";
import {
  Checkbox,
  Button,
  FormControlLabel,
  useMediaQuery,
  TextareaAutosize,
  Snackbar,
  Alert,
  Slide,
  CircularProgress,
  Box,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import AnseraLogo from "assets/images/Anser.png";
import { useSettings } from "context/SettingsContext";
import { useSystemTheme } from "context/SystemThemeContext";

const DefaultQuestions = () => {
  const [enabled, setEnabled] = useState(false);
  const [questions, setQuestions] = useState(["", "", "", "", ""]);
  const [originalQuestions, setOriginalQuestions] = useState(["", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { mode } = useSystemTheme();
  const isDarkMode = mode === "dark";
  const labelWidth = isMobile ? "100%" : "240px";
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [isDataSyncing, setIsDataSyncing] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  const { settings, updateSettings, loading: settingsLoading } = useSettings();

  useEffect(() => {
    if (settings) {
      try {
        const questionsFromSettings = settings.ansera_search_default_questions;
        
        if (!questionsFromSettings || 
            questionsFromSettings === null || 
            questionsFromSettings === '[]' ||
            (typeof questionsFromSettings === 'string' && questionsFromSettings.trim() === '')) {
          
          setIsDataSyncing(true);
          setQuestions(["", "", "", "", ""]);
          setOriginalQuestions(["", "", "", "", ""]);
          setEnabled(false);
          return;
        }
        
        setIsDataSyncing(false);
        setRetryCount(0);
        
        let parsedQuestions = ["", "", "", "", ""];
        
        if (questionsFromSettings) {
          if (typeof questionsFromSettings === 'string') {
            try {
              parsedQuestions = JSON.parse(questionsFromSettings);
            } catch (parseError) {
              console.error("Error parsing questions JSON:", parseError);
              if (questionsFromSettings.trim() !== '') {
                parsedQuestions[0] = questionsFromSettings;
              }
            }
          } else if (Array.isArray(questionsFromSettings)) {
            parsedQuestions = questionsFromSettings;
          }
          
          while (parsedQuestions.length < 5) {
            parsedQuestions.push("");
          }
          if (parsedQuestions.length > 5) {
            parsedQuestions = parsedQuestions.slice(0, 5);
          }
        }
        
        setQuestions(parsedQuestions);
        setEnabled(settings.ansera_search_display_default_questions === 1);
        setOriginalQuestions([...parsedQuestions]);
      } catch (error) {
        console.error("Error processing questions:", error);
        setIsDataSyncing(true);
        const defaultQuestions = ["", "", "", "", ""];
        setQuestions(defaultQuestions);
        setOriginalQuestions([...defaultQuestions]);
        setEnabled(false);
      }
    }
  }, [settings, retryCount]);

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
  };

  const handleQuestionChange = (index, value) => {
    if (isDataSyncing) return;
    const updated = [...questions];
    updated[index] = value;
    setQuestions(updated);
  };

  const hasChanges = JSON.stringify(questions) !== JSON.stringify(originalQuestions) || 
                    enabled !== (settings?.ansera_search_display_default_questions === 1);

  const handleSave = async () => {
    if (isDataSyncing) {
      setSnackbarMessage("Data sync is in progress. Please wait...");
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
      return;
    }

    try {
      setLoading(true);
      const questionsToSave = Array.isArray(questions) ? questions : [];
      const updates = {
        ansera_search_default_questions: JSON.stringify(questionsToSave),
        ansera_search_display_default_questions: enabled ? 1 : 0,
      };

      await updateSettings(updates);
      
      setOriginalQuestions([...questions]);
      setSnackbarMessage("Questions updated successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Failed to save settings:", error);
      setSnackbarMessage("Failed to save Questions!");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  if (settingsLoading && !settings) {
    return (
      <SoftBox
        sx={{
          backgroundColor: isDarkMode ? "#060C18" : "#fff",
          p: isMobile ? 2 : 4,
          borderRadius: "16px",
          boxShadow: isDarkMode 
            ? "0px 6px 20px rgba(0, 0, 0, 0.3)" 
            : "0px 6px 20px rgba(0, 0, 0, 0.06)",
          textAlign: "center",
          py: 4,
          transition: "background-color 0.3s ease",
        }}
      >
        <CircularProgress size={40} sx={{ color: "#219297", mb: 2 }} />
        <SoftTypography variant="h6" fontWeight="medium" sx={{ color: isDarkMode ? "#e2e8f0" : undefined }}>
          Loading settings...
        </SoftTypography>
      </SoftBox>
    );
  }

  if (isDataSyncing) {
    return (
      <SoftBox
        sx={{
          backgroundColor: isDarkMode ? "#060C18" : "#fff",
          p: isMobile ? 2 : 4,
          borderRadius: "16px",
          boxShadow: isDarkMode 
            ? "0px 6px 20px rgba(0, 0, 0, 0.3)" 
            : "0px 6px 20px rgba(0, 0, 0, 0.06)",
          textAlign: "center",
          py: 6,
          transition: "background-color 0.3s ease",
        }}
      >
        <SoftTypography variant="h6" fontWeight="bold" sx={{ color: "#219297" }} gutterBottom>
          Data Sync in Progress
        </SoftTypography>
        <SoftTypography variant="body1" sx={{ color: isDarkMode ? "#a0aec0" : "text.secondary" }} paragraph>
          Your default questions will appear once data sync is completed.
        </SoftTypography>
        <SoftTypography variant="body2" sx={{ color: isDarkMode ? "#a0aec0" : "text.secondary" }} paragraph>
          Please wait a few moments for the sync to complete, then try again.
        </SoftTypography>
        
        <Box sx={{ mt: 3, display: "flex", justifyContent: "center", gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => window.location.reload()}
            sx={{
              borderColor: "#219297",
              color: "#219297",
              fontWeight: 600,
              textTransform: "none",
              px: 3,
              py: 1,
              borderRadius: "8px",
              "&:hover": {
                backgroundColor: "rgba(33, 146, 151, 0.1)",
                borderColor: "#1a767a",
              },
            }}
          >
            Refresh Page
          </Button>
        </Box>

        <Box sx={{ mt: 1, p: 1, backgroundColor: isDarkMode ? "#0d1425" : "#ffffff", borderRadius: "8px" }}>
          <SoftTypography variant="caption" sx={{ color: isDarkMode ? "#a0aec0" : "text.secondary" }}>
            <strong>Note:</strong> This usually takes a few minutes.
          </SoftTypography>
        </Box>
      </SoftBox>
    );
  }

  if (!Array.isArray(questions)) {
    console.error("Questions is not an array:", questions);
    setQuestions(["", "", "", "", ""]);
    return (
      <SoftBox
        sx={{
          backgroundColor: isDarkMode ? "#060C18" : "#fff",
          p: isMobile ? 2 : 4,
          borderRadius: "16px",
          boxShadow: isDarkMode 
            ? "0px 6px 20px rgba(0, 0, 0, 0.3)" 
            : "0px 6px 20px rgba(0, 0, 0, 0.06)",
          textAlign: "center",
          py: 4,
        }}
      >
        <SoftTypography variant="h6" sx={{ color: isDarkMode ? "#e2e8f0" : "error.main" }}>
          Error loading questions
        </SoftTypography>
        <Button
          variant="contained"
          onClick={() => window.location.reload()}
          sx={{ mt: 2, backgroundColor: "#219297", "&:hover": { backgroundColor: "#1a767a" } }}
        >
          Refresh
        </Button>
      </SoftBox>
    );
  }

  return (
    <SoftBox
      sx={{
        backgroundColor: isDarkMode ? "#060C18" : "#fff",
        p: isMobile ? 2 : 4,
        borderRadius: "16px",
        boxShadow: isDarkMode 
          ? "0px 6px 20px rgba(0, 0, 0, 0.3)" 
          : "0px 6px 20px rgba(0, 0, 0, 0.06)",
        position: "relative",
        opacity: isDataSyncing ? 0.6 : 1,
        transition: "background-color 0.3s ease, box-shadow 0.3s ease",
      }}
      display="flex"
      flexDirection="column"
      gap={2.5}
    >
      {isDataSyncing && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: isDarkMode ? "rgba(6, 12, 24, 0.8)" : "rgba(255, 255, 255, 0.8)",
            zIndex: 10,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "16px",
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            <CircularProgress size={40} sx={{ color: "#219297", mb: 2 }} />
            <Typography variant="body1" sx={{ color: isDarkMode ? "#e2e8f0" : "text.secondary" }}>
              Data sync in progress...
            </Typography>
          </Box>
        </Box>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        TransitionComponent={(props) => <Slide {...props} direction="top" />}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          variant="filled"
          sx={{
            width: "100%",
            color: "#ffffff",
            backgroundColor:
              snackbarSeverity === "success"
                ? "#008080" 
                : snackbarSeverity === "error"
                ? "#d32f2f"
                : snackbarSeverity === "warning"
                ? "#ff9800"
                : undefined,
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <SoftBox display="flex" alignItems="center" gap={1}>
        <img 
          src={AnseraLogo} 
          alt="Ansera Logo" 
          width={64}
          style={{
            filter: isDarkMode ? "brightness(0) invert(1)" : "none",
            transition: "filter 0.3s ease",
          }}
        />
        <SoftTypography variant="h6" fontWeight="bold" sx={{ color: isDarkMode ? "#e2e8f0" : undefined }}>
          Default Questions
        </SoftTypography>
      </SoftBox>

      <SoftTypography
        variant="body2"
        sx={{
          borderBottom: `1px solid ${isDarkMode ? "#1a2542" : "#e0e0e0"}`,
          pb: 1,
          mb: 1,
          color: isDarkMode ? "#a0aec0" : "text.secondary",
          transition: "border-color 0.3s ease",
        }}
      >
        Display up to 5 default search questions that will appear on a visitor's first interaction.
      </SoftTypography>

      <SoftBox
        display="flex"
        flexDirection={isMobile ? "column" : "row"}
        alignItems="center"
        gap={1.5}
      >
        <SoftBox width={labelWidth}>
          <SoftTypography variant="button" fontWeight="medium" sx={{ color: isDarkMode ? "#e2e8f0" : undefined }}>
            Display Default Questions
          </SoftTypography>
        </SoftBox>
        <FormControlLabel
          control={
            <Checkbox
              checked={enabled}
              onChange={() => !isDataSyncing && setEnabled((prev) => !prev)}
              disabled={isDataSyncing}
              sx={{
                color: isDarkMode ? "#a0aec0" : undefined,
                '&.Mui-checked': {
                  color: "#219297",
                },
              }}
            />
          }
          label={
            <span style={{ color: isDarkMode ? "#e2e8f0" : undefined }}>
              {enabled ? "Enabled" : "Disabled"}
            </span>
          }
        />
      </SoftBox>

      <SoftBox>
        <SoftTypography variant="button" fontWeight="medium" mb={1} sx={{ color: isDarkMode ? "#e2e8f0" : undefined }}>
          Questions:
        </SoftTypography>
        <SoftBox mt={1} display="flex" flexDirection="column" gap={2}>
          {questions.map((q, i) => (
            <TextareaAutosize
              key={i}
              value={q}
              onChange={(e) => handleQuestionChange(i, e.target.value)}
              placeholder={`Question ${i + 1}`}
              minRows={2}
              disabled={isDataSyncing}
              style={{
                width: "100%",
                fontSize: "14px",
                fontFamily: `"Roboto","Helvetica","Arial",sans-serif`,
                padding: "8px",
                borderRadius: "10px",
                border: isDarkMode ? "1px solid #1a2542" : "1px solid #ccc",
                outline: "none",
                resize: "vertical",
                boxShadow: isDarkMode ? "inset 0 1px 2px rgba(0,0,0,0.3)" : "inset 0 1px 2px rgba(0,0,0,0.1)",
                transition: "border-color 0.3s ease",
                backgroundColor: isDarkMode 
                  ? (isDataSyncing ? "#0d1425" : "#060C18")
                  : (isDataSyncing ? "#f5f5f5" : "#fff"),
                color: isDarkMode ? "#e2e8f0" : "#344767",
                cursor: isDataSyncing ? "not-allowed" : "text",
                opacity: isDataSyncing ? 0.7 : 1,
              }}
              onFocus={(e) => !isDataSyncing && (e.target.style.border = "1px solid #219297")}
              onBlur={(e) => !isDataSyncing && (e.target.style.border = isDarkMode ? "1px solid #1a2542" : "1px solid #ccc")}
            />
          ))}
        </SoftBox>
      </SoftBox>

      <SoftBox mt={2}>
        <Button
          variant="contained"
          size="medium"
          disabled={!hasChanges || loading || settingsLoading || isDataSyncing}
          onClick={handleSave}
          sx={{
            backgroundColor: isDataSyncing ? (isDarkMode ? "#1a2542" : "#cccccc") : "#219297",
            color: "#fff",
            fontWeight: 600,
            textTransform: "none",
            px: 3,
            py: 1,
            borderRadius: "8px",
            "&:hover": {
              backgroundColor: isDataSyncing ? (isDarkMode ? "#1a2542" : "#cccccc") : "#1a767a",
            },
            opacity: !hasChanges || loading || isDataSyncing ? 0.5 : 1,
            cursor: !hasChanges || loading || isDataSyncing ? "not-allowed" : "pointer",
          }}
        >
          {loading ? "Saving..." : isDataSyncing ? "Data Syncing..." : "Save Changes"}
        </Button>
        
        {isDataSyncing && (
          <SoftTypography variant="caption" sx={{ display: "block", mt: 1, color: isDarkMode ? "#a0aec0" : "text.secondary" }}>
            Data sync is in progress. Please try again after sync completes.
          </SoftTypography>
        )}
      </SoftBox>
    </SoftBox>
  );
};

export default DefaultQuestions;